import React, { useEffect, useMemo, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminListToolbar from '../../components/admin/AdminListToolbar';
import AutoApprovalSettingCard from '../../components/admin/AutoApprovalSettingCard';
import Pagination from '../../components/Pagination';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError } from '../../utils/toast';

const AdminRequestsContent = () => {
  const { isCollapsed } = useSidebar();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('a-z');
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchPendingBusinesses();
  }, []);

  const fetchPendingBusinesses = async () => {
    try {
      const response = await api.get('/admin/pending-businesses');
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching pending businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSorted = useMemo(() => {
    let list = businesses;
    const s = (search || '').toLowerCase().trim();
    if (s) {
      list = list.filter((b) =>
        (b.name || '').toLowerCase().includes(s) ||
        (b.category?.name || '').toLowerCase().includes(s) ||
        (b.city?.name || '').toLowerCase().includes(s)
      );
    }
    list = [...list].sort((a, b) => {
      const na = (a.name || '').toLowerCase();
      const nb = (b.name || '').toLowerCase();
      return sort === 'z-a' ? nb.localeCompare(na) : na.localeCompare(nb);
    });
    return list;
  }, [businesses, search, sort]);

  const totalItems = filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const paginatedList = useMemo(
    () => filteredAndSorted.slice((currentPage - 1) * limit, currentPage * limit),
    [filteredAndSorted, currentPage, limit]
  );

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.patch(`/businesses/${id}/status`, { status: newStatus });
      fetchPendingBusinesses();
      const statusMessage = newStatus === 'approved' ? 'Business approved successfully!' : 'Business rejected.';
      showSuccess(statusMessage);
    } catch (error) {
      showError(error.response?.data?.message || 'Error updating status');
    }
  };

  return (
    <>
      <SEO title="Pending Business Requests - Admin" description="Pending business requests." noindex />

        <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="container-custom py-6 md:py-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50 mb-6 md:mb-8">Pending Business Requests</h1>

            {/* Auto-Approval Setting Radio Card */}
            <div className="mb-6">
              <AutoApprovalSettingCard onStatusChange={() => fetchPendingBusinesses()} />
            </div>

            {!loading && (
              <AdminListToolbar
                searchInput={searchInput}
                onSearchInputChange={setSearchInput}
                search={search}
                onSearch={() => { setSearch(searchInput); setCurrentPage(1); }}
                onClear={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
                sort={sort}
                onSortChange={(v) => { setSort(v); setCurrentPage(1); }}
                limit={limit}
                onLimitChange={(v) => { setLimit(v); setCurrentPage(1); }}
                searchPlaceholder="Search by name, category, or city..."
              />
            )}

            {loading ? (
              <PageLoader message="Loading requests..." />
        ) : paginatedList.length > 0 ? (
          <>
          <div className="space-y-4 md:space-y-6">
            {paginatedList.map((business, index) => {
              const srNo = (currentPage - 1) * limit + index + 1;
              return (
              <div key={business._id} className="card p-4 md:p-6 animate-scale-in" style={{ animationDelay: `${index * 50}ms` }}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{srNo}.</span>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-50">{business.name}</h2>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base">
                      {business.category?.name} • {business.city?.name}
                    </p>
                  </div>
                  <span className="badge badge-warning text-sm">
                    Pending
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base">
                      <span className="font-semibold">Address:</span> {business.address}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base">
                      <span className="font-semibold">Phone:</span> {business.phone}
                    </p>
                    {business.email && (
                      <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base">
                        <span className="font-semibold">Email:</span> {business.email}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    {business.description && (
                      <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base">
                        <span className="font-semibold">Description:</span> {business.description}
                      </p>
                    )}
                    <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base">
                      <span className="font-semibold">Created By:</span> {business.createdBy}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleStatusChange(business._id, 'approved')}
                    className="btn-primary flex-1 text-sm"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleStatusChange(business._id, 'rejected')}
                    className="btn-outline flex-1 text-sm text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                  >
                    Reject
                  </button>
                </div>
              </div>
            );})}
          </div>

          {totalPages >= 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                itemsOnCurrentPage={paginatedList.length}
                onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              />
            </div>
          )}
          </>
        ) : (
          <div className="card p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-lg">{search ? 'No requests match your search.' : 'No pending business requests.'}</p>
          </div>
            )}
          </div>
        </main>
    </>
  );
};

const AdminRequests = () => {
  return (
    <AdminLayout>
      <AdminRequestsContent />
    </AdminLayout>
  );
};

export default AdminRequests;

